import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { FileText, Plus, Minus, Trash2, Search, Eye, Calculator, ArrowRight } from "lucide-react";
import { api, inputCls, labelCls, rd, invoiceWhatsAppUrl } from "./posShared";
import InvoicePrint from "./InvoicePrint";

export default function Cotizaciones({ products, customers, settings, reloadCustomers, onConvert }) {
  const [cart, setCart] = useState([]);
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [applyItbis, setApplyItbis] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(null);
  const [quotes, setQuotes] = useState([]);

  const loadQuotes = useCallback(() => api.get("/pos/quotes").then((r) => setQuotes(r.data)).catch(() => {}), []);
  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  const today = new Date().toISOString().slice(0, 10);
  const effStatus = (qz) => {
    const st = qz.status || "pendiente";
    if (st === "pendiente" && qz.valid_until && qz.valid_until < today) return "vencida";
    return st;
  };
  const STATUS = {
    pendiente: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    aprobada: { label: "Aprobada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    vencida: { label: "Vencida", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
    facturada: { label: "Facturada", cls: "bg-rose/10 text-rose border-rose/20" },
  };
  const changeStatus = async (qz, status) => {
    try {
      await api.put(`/pos/quotes/${qz.id}/status`, { status });
      toast.success(`Cotización ${qz.number}: ${STATUS[status].label}`);
      loadQuotes();
    } catch (e) { toast.error("No se pudo actualizar el estado"); }
  };

  const selectedCust = customers.find((c) => c.id === customerId);
  const filtered = q ? products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.code || "").toLowerCase().includes(q.toLowerCase())) : products;

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { product_id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
  };
  const setQty = (pid, d) => setCart((c) => c.map((i) => i.product_id === pid ? { ...i, qty: Math.max(1, i.qty + d) } : i));
  const removeItem = (pid) => setCart((c) => c.filter((i) => i.product_id !== pid));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const itbisRate = settings?.itbis_rate || 18;
  const itbis = applyItbis ? subtotal * itbisRate / 100 : 0;
  const total = subtotal + itbis;

  const autoSend = async (quote, cust) => {
    if (!cust) return;
    if (settings?.auto_send_email && cust.email) {
      try {
        await api.post(`/pos/quotes/${quote.id}/email`, { to: cust.email });
        quote._sentEmail = cust.email;
        toast.success(`Cotización enviada a ${cust.email}`);
      } catch (e) { toast.error("No se pudo enviar el correo automático"); }
    }
    if (settings?.auto_send_whatsapp && (cust.whatsapp || cust.phone)) {
      const url = invoiceWhatsAppUrl(quote, settings, cust.whatsapp || cust.phone);
      const win = window.open(url, "_blank");
      quote._sentWhatsApp = cust.whatsapp || cust.phone;
      if (!win) toast("Ventana de WhatsApp bloqueada", { action: { label: "Abrir", onClick: () => window.open(url, "_blank") } });
    }
  };

  const generate = async () => {
    if (!cart.length) return toast.error("Agrega al menos un producto");
    setSaving(true);
    try {
      const body = {
        customer_id: customerId || null,
        customer_name: selectedCust ? selectedCust.name : "Cliente Final",
        customer_address: selectedCust?.address || "",
        items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, price: i.price })),
        apply_itbis: applyItbis,
        valid_until: validUntil,
        notes,
      };
      const { data } = await api.post("/pos/quotes", body);
      await autoSend(data, selectedCust);
      setView(data);
      setCart([]); setCustomerId(""); setApplyItbis(false); setValidUntil(""); setNotes("");
      loadQuotes();
      toast.success(`Cotización ${data.number} generada`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar la cotización");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Calculator size={22} className="text-rose" />
        <h1 className="font-serif text-3xl">Cotizaciones</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Productos */}
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl p-5">
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="cot-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto por nombre o código..." className={`${inputCls} pl-9`} />
          </div>
          <div className="grid sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto">
            {filtered.slice(0, 60).map((p) => (
              <button key={p.id} data-testid={`cot-add-${p.id}`} onClick={() => addToCart(p)}
                className="text-left border border-neutral-200 rounded-lg p-3 hover:border-rose transition">
                <div className="font-medium text-sm text-espresso truncate">{p.name}</div>
                <div className="text-xs text-neutral-500">{rd(p.price)}</div>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-sm text-neutral-400 col-span-2 py-6 text-center">Sin productos.</p>}
          </div>
        </div>

        {/* Resumen / cotización */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
          <div>
            <label className={labelCls}>Cliente</label>
            <select data-testid="cot-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={inputCls}>
              <option value="">Cliente Final</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {selectedCust && !selectedCust.email && !(selectedCust.whatsapp || selectedCust.phone) && (
              <p className="text-xs text-amber-600 mt-1">Este cliente no tiene correo ni WhatsApp para enviarle la cotización.</p>
            )}
          </div>

          <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="cot-cart">
            {cart.length === 0 && <p className="text-sm text-neutral-400 text-center py-4">Agrega productos a la cotización.</p>}
            {cart.map((i) => (
              <div key={i.product_id} className="flex items-center gap-2 text-sm border-b border-neutral-50 pb-2">
                <div className="flex-1 min-w-0">
                  <div className="truncate text-espresso">{i.name}</div>
                  <div className="text-xs text-neutral-500">{rd(i.price)} · {rd(i.price * i.qty)}</div>
                </div>
                <button onClick={() => setQty(i.product_id, -1)} className="p-1 border border-neutral-200 rounded"><Minus size={13} /></button>
                <span className="w-6 text-center">{i.qty}</span>
                <button onClick={() => setQty(i.product_id, 1)} className="p-1 border border-neutral-200 rounded"><Plus size={13} /></button>
                <button onClick={() => removeItem(i.product_id)} className="p-1 text-neutral-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm"><input data-testid="cot-itbis" type="checkbox" checked={applyItbis} onChange={(e) => setApplyItbis(e.target.checked)} /> Incluir ITBIS ({itbisRate}%)</label>
          <div>
            <label className={labelCls}>Válida hasta (opcional)</label>
            <input data-testid="cot-valid" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={inputCls} />
          </div>

          <div className="text-sm space-y-1 pt-2 border-t border-neutral-100">
            <div className="flex justify-between"><span>Subtotal</span><span>{rd(subtotal)}</span></div>
            {applyItbis && <div className="flex justify-between text-neutral-500"><span>ITBIS {itbisRate}%</span><span>{rd(itbis)}</span></div>}
            <div className="flex justify-between font-bold text-lg pt-1"><span>Total</span><span>{rd(total)}</span></div>
          </div>

          <button data-testid="cot-generate" onClick={generate} disabled={saving} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition disabled:opacity-60">
            {saving ? "Generando..." : "Generar cotización"}
          </button>
        </div>
      </div>

      {/* Historial */}
      <div className="mt-6 bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-100 flex items-center gap-2"><FileText size={16} className="text-rose" /><h2 className="font-medium">Cotizaciones recientes</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500">
            <tr><th className="px-4 py-2.5 text-left">Número</th><th className="px-4 py-2.5 text-left">Cliente</th><th className="px-4 py-2.5 text-left">Fecha</th><th className="px-4 py-2.5 text-right">Total</th><th className="px-4 py-2.5 text-center">Estado</th><th className="px-4 py-2.5 text-right">Acciones</th></tr>
          </thead>
          <tbody>
            {quotes.map((qz) => {
              const es = effStatus(qz);
              return (
              <tr key={qz.id} data-testid={`cot-row-${qz.id}`} className="border-t border-neutral-50">
                <td className="px-4 py-2.5 font-medium">{qz.number}</td>
                <td className="px-4 py-2.5 text-neutral-600">{qz.customer_name}</td>
                <td className="px-4 py-2.5 text-neutral-500">{(qz.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5 text-right">{rd(qz.total)}</td>
                <td className="px-4 py-2.5 text-center">
                  {es === "facturada" ? (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${STATUS.facturada.cls}`}>Facturada</span>
                  ) : (
                    <select data-testid={`cot-status-${qz.id}`} value={es} onChange={(e) => changeStatus(qz, e.target.value)}
                      className={`text-xs border rounded-full px-2 py-1 focus:outline-none ${STATUS[es].cls}`}>
                      <option value="pendiente">Pendiente</option>
                      <option value="aprobada">Aprobada</option>
                      <option value="vencida">Vencida</option>
                    </select>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1.5 justify-end">
                    <button data-testid={`cot-view-${qz.id}`} onClick={() => setView(qz)} title="Ver" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Eye size={14} /></button>
                    {qz.status !== "facturada" && (
                      <button data-testid={`cot-convert-${qz.id}`} onClick={() => onConvert && onConvert(qz)} title="Convertir a factura"
                        className="inline-flex items-center gap-1.5 border border-rose text-rose rounded-lg px-3 py-1.5 text-xs hover:bg-rose hover:text-cream transition">
                        <ArrowRight size={13} /> Convertir a factura
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {quotes.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">No hay cotizaciones.</td></tr>}
          </tbody>
        </table>
      </div>

      {view && <InvoicePrint invoice={view} settings={settings} customers={customers} onClose={() => setView(null)} />}
    </div>
  );
}
